<template>
  <div>
    <div class="wg-kpd-head">
      <h2 class="wg-kpd-title">Where you show up on Google</h2>
      <span class="wg-kpd-total">
        {{ totalNow }} searches tracked
        <template v-if="prevLabel"> · {{ deltaTotal >= 0 ? '+' : '' }}{{ deltaTotal }} vs {{ prevLabel }}</template>
      </span>
    </div>

    <div class="wg-dist">
      <div class="wg-dist-row">
        <span class="rl">Now</span>
        <div class="wg-dist-bar">
          <i
            v-for="bucket in buckets"
            :key="bucket.id"
            :style="{ width: widthOf(now[bucket.id], totalNow), background: bucket.color }"
            :title="`${bucket.label}: ${now[bucket.id] || 0}`"
          ></i>
        </div>
      </div>
      <div class="wg-dist-row ghost">
        <span class="rl">Was</span>
        <div class="wg-dist-bar">
          <i
            v-for="bucket in buckets"
            :key="bucket.id"
            :style="{ width: widthOf(prev[bucket.id], totalPrev), background: bucket.color }"
          ></i>
        </div>
      </div>

      <div class="wg-dist-legend">
        <div v-for="bucket in buckets" :key="bucket.id" class="wg-dist-item">
          <span class="sw" :style="{ background: bucket.color }"></span>
          <span class="ll">{{ bucket.label }}</span>
          <span class="nn">{{ now[bucket.id] || 0 }}</span>
          <span class="wg-delta" :class="deltaClass(bucket.id)">{{ deltaText(bucket.id) }}</span>
        </div>
      </div>
      <div class="wg-dist-note">
        Page 1 is what most people look at. The goal each {{ periodWord }} is to move more searches
        into the green section on the left.
      </div>
    </div>
  </div>
</template>

<script>
const BUCKETS = [
  { id: 'page1', label: 'Page 1', color: '#00CD49' },
  { id: 'page2', label: 'Page 2', color: '#FFB95A' },
  { id: 'page3', label: 'Page 3+', color: '#CCCCCC' },
]

export default {
  name: 'WinsPositionDistribution',
  props: {
    now: {
      type: Object,
      default: () => ({}),
    },
    prev: {
      type: Object,
      default: () => ({}),
    },
    prevLabel: {
      type: String,
      default: '',
    },
    periodWord: {
      type: String,
      default: 'quarter',
    },
  },
  computed: {
    buckets () {
      return BUCKETS
    },
    totalNow () {
      return BUCKETS.reduce((total, b) => total + (this.now[b.id] || 0), 0)
    },
    totalPrev () {
      return BUCKETS.reduce((total, b) => total + (this.prev[b.id] || 0), 0)
    },
    deltaTotal () {
      return this.totalNow - this.totalPrev
    },
  },
  methods: {
    widthOf (value, total) {
      if (!total) return '0%'
      return ((value || 0) / total * 100) + '%'
    },
    deltaOf (bucketId) {
      return (this.now[bucketId] || 0) - (this.prev[bucketId] || 0)
    },
    deltaText (bucketId) {
      const delta = this.deltaOf(bucketId)
      if (delta === 0) return '±0'
      return (delta > 0 ? '▲' : '▼') + Math.abs(delta)
    },
    deltaClass (bucketId) {
      const delta = this.deltaOf(bucketId)
      return delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../Settings/wg-tokens.scss';

/* keyword distribution */
.wg-kpd-head {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.wg-kpd-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: $pop-teal;
  margin: 0;
}

.wg-kpd-total {
  font-size: 0.8rem;
  color: $pop-ink-muted;
  white-space: nowrap;
}

.wg-dist {
  background: #fff;
  border: 1px solid $wg-panel-line;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 28px;
}

.wg-dist-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 9px;

  .rl {
    width: 26px;
    flex: none;
    font-size: 0.7rem;
    font-weight: 700;
    color: $pop-ink-muted;
    text-align: right;
  }

  &.ghost {
    margin-bottom: 16px;
    opacity: 0.42;

    .wg-dist-bar {
      height: 9px;
    }
  }
}

.wg-dist-bar {
  flex: 1;
  display: flex;
  height: 22px;
  border-radius: 4px;
  overflow: hidden;
  background: $pop-chart-track;

  i {
    display: block;
    height: 100%;
  }
}

.wg-dist-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 26px;
  border-top: 1px solid $wg-hairline;
  padding-top: 15px;
}

.wg-dist-item {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  .sw {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex: none;
  }

  .ll {
    font-size: 0.85rem;
    color: $pop-ink-muted;
  }

  .nn {
    font-size: 0.95rem;
    font-weight: 700;
    color: $pop-teal;
  }
}

.wg-delta {
  font-size: 0.7rem;
  font-weight: 700;

  &.up {
    color: $pop-success;
  }

  &.down {
    color: $pop-danger;
  }

  &.flat {
    color: $pop-ink-faint;
  }
}

.wg-dist-note {
  font-size: 0.85rem;
  line-height: 1.5;
  color: $pop-ink-muted;
  text-wrap: pretty;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid $wg-hairline;
}
</style>
